import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  ExceptionTinyResponse,
  PunchTinyResponse,
  SchedulePlaceholderUpdateRequestV2,
  TimeCardApiApiV2TimecardPayDateCommentDeleteRequest,
  TimeCardApiApiV2TimecardPayDateCommentPostRequest,
  TimeCardApiApiV2TimecardPayPeriodApprovalButtonEmployeeIdGetRequest,
  TimeCardApiApiV2TimecardPayPeriodApprovalEmployeeIdPostRequest,
  TimeCardApiApiV2TimecardPayPeriodCommentGetRequest,
  TimeCardApiApiV2TimecardPayPeriodCommentPostRequest,
  TimeCardApiApiV2TimecardPlaceholdersEmployeeIdGetRequest,
  TimeCardApiApiV2TimecardRecomputePostRequest,
  useApi,
} from '../index';
import {
  EmployeeCacheKeys,
  HourCacheKeys,
  ScheduleCacheKeys,
  TimeCardCacheKeys,
} from './util/cache-keys';

const useUpdateCache = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    queryClient.invalidateQueries([HourCacheKeys.DAILY_SUMMARY]).catch(noop);
    queryClient
      .invalidateQueries([HourCacheKeys.PAY_PERIOD_SUMMARY])
      .catch(noop);
    queryClient
      .invalidateQueries([ScheduleCacheKeys.EMPLOYEE_SCHEDULES])
      .catch(noop);
    queryClient
      .invalidateQueries([ScheduleCacheKeys.EMPLOYEE_PLACEHOLDERS])
      .catch(noop);
    queryClient
      .invalidateQueries([TimeCardCacheKeys.PAY_PERIOD_APPROVAL])
      .catch(noop);
    queryClient
      .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
      .catch(noop);
    await queryClient
      .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
      .catch(noop);
  }, [queryClient]);
};

export const useCalculateTimeCard = () => {
  const api = useApi();
  const { notifySuccess, notifyError } = useSnackBar();
  const updateCache = useUpdateCache();

  return useMutation(
    (body: TimeCardApiApiV2TimecardRecomputePostRequest) => {
      return api.timeCardApi.apiV2TimecardRecomputePost(body);
    },
    {
      onSuccess: async () => {
        await updateCache();
        notifySuccess('Recalculation was successful');
      },
      onError: async () => {
        notifyError('Recalculation failed');
      },
    }
  );
};

export const useAddEditPayDateComment = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    (body: TimeCardApiApiV2TimecardPayDateCommentPostRequest) => {
      return api.timeCardApi.apiV2TimecardPayDateCommentPost(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeTimeCardGet = (
  employeeId: number,
  startDate?: string,
  endDate?: string
) => {
  const api = useApi();
  const response = useQuery({
    queryKey: [
      TimeCardCacheKeys.EMPLOYEE_TIME_CARD,
      { employeeId, startDate, endDate },
    ],
    queryFn: () =>
      api.timeCardApi
        .apiV2TimecardEmployeeIdGet({ employeeId, startDate, endDate })
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(employeeId) && Boolean(startDate),
  });
  return {
    ...response,
    hours: response.data?.hours ?? [],
    exceptions: response.data?.exceptions ?? [],
    punches: response.data?.punches ?? [],
    employeeHolidays: response.data?.employeeHolidays ?? [],
    otherHours: response.data?.otherHours ?? [],
    payDateComments: response.data?.payDateComments ?? [],
    otherHoursForActualDate: response.data?.otherHoursForActualDate ?? [],
    payPeriodComments: response.data?.payPeriodComments ?? [],
    placeholders: response.data?.placeholders ?? [],
    punchNotifications: response.data?.punchNotifications ?? [],
    calculationNotes: response.data?.calculationNotes ?? [],
  };
};

export type TimeCardResponseType = ReturnType<typeof useEmployeeTimeCardGet>;
export type PunchResponse = PunchTinyResponse;
export type ExceptionResponse = ExceptionTinyResponse;

export const useDeletePayDateComment = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    (body: TimeCardApiApiV2TimecardPayDateCommentDeleteRequest) => {
      return api.timeCardApi.apiV2TimecardPayDateCommentDelete(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeTimeCardPlaceholderGet = (
  body: TimeCardApiApiV2TimecardPlaceholdersEmployeeIdGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [TimeCardCacheKeys.TIME_CARD_PLACEHOLDERS, body],
    queryFn: () =>
      api.timeCardApi
        .apiV2TimecardPlaceholdersEmployeeIdGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.employeeId),
  });
};

export type PlaceholdersItemResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useEmployeeTimeCardPlaceholderGet>['data']>
>;

export const useUpdatePlaceholder = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(
    (body: SchedulePlaceholderUpdateRequestV2) => {
      return api.timeCardApi.apiV2TimecardPlaceholderPut({
        schedulePlaceholderUpdateRequestV2: body,
      });
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.TIME_CARD_PLACEHOLDERS])
          .catch(noop);
        queryClient
          .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
          .catch(noop);
        queryClient.invalidateQueries([HourCacheKeys.OTHER_HOURS]).catch(noop);
        queryClient
          .invalidateQueries([ScheduleCacheKeys.EMPLOYEE_PLACEHOLDERS])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const usePayPeriodCommentGet = (
  body: TimeCardApiApiV2TimecardPayPeriodCommentGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [TimeCardCacheKeys.PAY_PERIOD_COMMENT, body],
    queryFn: () =>
      api.timeCardApi
        .apiV2TimecardPayPeriodCommentGet(body)
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAddPayPeriodComment = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: TimeCardApiApiV2TimecardPayPeriodCommentPostRequest) => {
      return api.timeCardApi.apiV2TimecardPayPeriodCommentPost(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.PAY_PERIOD_COMMENT])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
    }
  );
};

export const useAddPayPeriodApproval = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (body: TimeCardApiApiV2TimecardPayPeriodApprovalEmployeeIdPostRequest) => {
      return api.timeCardApi.apiV2TimecardPayPeriodApprovalEmployeeIdPost(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.PAY_PERIOD_APPROVAL])
          .catch(noop);
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const usePayPeriodApprovalGet = (
  body: TimeCardApiApiV2TimecardPayPeriodApprovalButtonEmployeeIdGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [TimeCardCacheKeys.PAY_PERIOD_APPROVAL, body],
    queryFn: () =>
      api.timeCardApi
        .apiV2TimecardPayPeriodApprovalButtonEmployeeIdGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
